import {
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  Timestamp,
} from 'firebase/firestore';

import { isArrayOf } from '@app/utils/array';
import { isObjectAndIsEmpty, isObjectAndIsNotEmpty } from '@app/utils/object';

const isTimestamp = <T>(x: T | Timestamp): x is Timestamp => {
  // Frontend Timestamp
  return x instanceof Timestamp && x.toDate && typeof x.toDate === 'function';
};

type ReplacedClientTimeStamp<T> = Replaced<T, Timestamp, Date>;

const convertFields = (
  data: DocumentData | ObjectValues<DocumentData>,
): ReplacedClientTimeStamp<typeof data> => {
  const convertedFields: ReplacedClientTimeStamp<typeof data> = {};
  if (isObjectAndIsEmpty(data)) {
    return data;
  } else if (isObjectAndIsNotEmpty(data) && !isTimestamp(data)) {
    Object.keys(data).forEach(key => {
      convertedFields[key] = convertFields(data[key]);
    });
  } else if (isArrayOf<unknown>(data)) {
    return data.map(convertFields);
  } else if (isTimestamp(data)) {
    return data.toDate();
  } else {
    return data;
  }

  return convertedFields;
};

/** This converter converts timestamps from Firestore to Date objects */
export default class ClientTimestampConverter<T extends ReplacedClientTimeStamp<DocumentData>>
  implements FirestoreDataConverter<T>
{
  toFirestore(modelObject: T): ReplacedClientTimeStamp<T> {
    // This conversion will happen automatically
    return modelObject as ReplacedClientTimeStamp<T>;
  }

  fromFirestore(snapshot: QueryDocumentSnapshot) {
    const data = snapshot.data();
    const convertedData = convertFields(data);
    return convertedData as T;
  }
}
